import { useState } from 'react';
import { FaMinus, FaPlus, FaSearch } from 'react-icons/fa';
import { FAQS } from '../constants/FrequentlyAskedQuestions';

const FAQ = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [openQuestion, setOpenQuestion] = useState(null);

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const visibleFaqs = FAQS.faqs.filter((faq) => {
        const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
        const searchableText = `${faq.question} ${faq.answer} ${faq.tags.join(' ')}`.toLowerCase();
        return matchesCategory && searchableText.includes(normalizedSearch);
    });

    const categoryCount = (categoryId) => FAQS.faqs.filter((faq) => faq.category === categoryId).length;

    return (
        <section className="min-h-screen py-16 sm:py-20 md:py-24 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 transition-colors duration-300">
            <div className="max-w-6xl mx-auto">
                <div className="max-w-3xl mb-10 sm:mb-14">
                    <p className="text-sm font-bold tracking-wide uppercase text-blue-600 dark:text-blue-400 mb-3">
                        A little clarity
                    </p>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 dark:text-white mb-5">
                        {FAQS.title}
                    </h1>
                    <p className="text-lg sm:text-xl leading-relaxed text-slate-600 dark:text-slate-300">
                        {FAQS.description}
                    </p>
                </div>

                <div className="max-w-2xl relative mb-8 sm:mb-10">
                    <FaSearch aria-hidden="true" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="search"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search questions and topics"
                        aria-label="Search frequently asked questions"
                        className="w-full py-3.5 pl-11 pr-4 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    />
                </div>

                <div className="flex gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide" aria-label="FAQ categories">
                    <button
                        type="button"
                        onClick={() => setActiveCategory('all')}
                        aria-pressed={activeCategory === 'all'}
                        className={`shrink-0 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeCategory === 'all'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500'
                        }`}
                    >
                        All questions <span className="ml-1 opacity-75">{FAQS.faqs.length}</span>
                    </button>
                    {FAQS.categories.map((category) => (
                        <button
                            key={category.id}
                            type="button"
                            onClick={() => setActiveCategory(category.id)}
                            aria-pressed={activeCategory === category.id}
                            className={`shrink-0 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeCategory === category.id
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500'
                            }`}
                        >
                            {category.label} <span className="ml-1 opacity-75">{categoryCount(category.id)}</span>
                        </button>
                    ))}
                </div>

                <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-3 mb-2">
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400" aria-live="polite">
                        {visibleFaqs.length} {visibleFaqs.length === 1 ? 'answer' : 'answers'}
                    </p>
                </div>

                {visibleFaqs.length > 0 ? (
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                        {visibleFaqs.map((faq) => {
                            const isOpen = openQuestion === faq.id;
                            const answerId = `faq-answer-${faq.id}`;

                            return (
                                <article key={faq.id} className="py-1">
                                    <h2>
                                        <button
                                            type="button"
                                            onClick={() => setOpenQuestion(isOpen ? null : faq.id)}
                                            aria-expanded={isOpen}
                                            aria-controls={answerId}
                                            className="group flex w-full items-center justify-between gap-6 py-5 sm:py-6 text-left"
                                        >
                                            <span className="text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                                                {faq.question}
                                            </span>
                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-slate-800 text-blue-700 dark:text-blue-300">
                                                {isOpen ? <FaMinus aria-hidden="true" className="text-xs" /> : <FaPlus aria-hidden="true" className="text-xs" />}
                                            </span>
                                        </button>
                                    </h2>
                                    <div id={answerId} hidden={!isOpen}>
                                        <p className="max-w-4xl pb-6 pr-10 text-base leading-relaxed text-slate-600 dark:text-slate-300">
                                            {faq.answer}
                                        </p>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <div className="py-16 text-center">
                        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">No matching questions</h2>
                        <p className="text-slate-600 dark:text-slate-400">Try another search term or choose a different category.</p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default FAQ;